import { queryClient } from '../query-client'
import { IPaginatedResult } from './paginated-result'
import { useDelete, useGet, usePost, usePut } from './reactQuery'
import { apiConsts } from './apiConsts'
import { useMutation } from 'react-query'
import { requestProvider } from './requestProvider'
import { newsQueryKeys } from 'src/query-keys'
import {
  ICreateNewsArticle,
  INewsArticle,
  INewsArticleFilter,
  INewsArticleListItem,
  IUpdateNewsArticle,
} from 'src/generated-types/news'
import { useInfiniteScroll } from 'src/components/table/useInfiniteScroll'

const customerBaseUrl = (customerId: EntityId) => `customers/${customerId}/news`
const adminBaseUrl = () => 'admin/news'

export const newsEndpoints = {
  getFeed: (customerId: EntityId) => `${customerBaseUrl(customerId)}/feed`,
  getCustomerNews: (customerId: EntityId) => `${customerBaseUrl(customerId)}`,
  getCustomerNewsArticle: (customerId: EntityId, id: EntityId) => `${customerBaseUrl(customerId)}/${id}`,
  createCustomerNews: (customerId: EntityId) => `${customerBaseUrl(customerId)}`,
  updateCustomerNews: (customerId: EntityId, id: EntityId) => `${customerBaseUrl(customerId)}/${id}`,
  deleteCustomerNews: (customerId: EntityId) => `${customerBaseUrl(customerId)}`,
  getAdminNews: () => adminBaseUrl(),
  getAdminNewsArticle: (id: EntityId) => `${adminBaseUrl()}/${id}`,
  createAdminNews: () => adminBaseUrl(),
  updateAdminNews: (id: EntityId) => `${adminBaseUrl()}/${id}`,
  deleteAdminNews: () => adminBaseUrl(),
  setNewsArticleStatus: (customerId: EntityId, id: EntityId) => `${customerBaseUrl(customerId)}/${id}/status`,
  setPinNewsArticle: (customerId: EntityId, id: EntityId) => `${customerBaseUrl(customerId)}/${id}/pin`,
  setAdminNewsArticleStatus: (id: EntityId) => `${adminBaseUrl()}/${id}/status`,
  setPinAdminNewsArticle: (id: EntityId) => `${adminBaseUrl()}/${id}/pin`,
}

export const invalidateNews = {
  getFeed(customerId?: EntityId) {
    queryClient.invalidateQueries(newsQueryKeys.GET_NEWS_FEED(customerId))
  },
  getCustomerNews(customerId?: EntityId) {
    queryClient.invalidateQueries(newsQueryKeys.GET_CUSTOMER_NEWS(customerId))
  },
  getAdminNews() {
    queryClient.invalidateQueries(newsQueryKeys.GET_ADMIN_NEWS())
  },
  getCustomerNewsArticle(customerId: EntityId, id: EntityId) {
    queryClient.invalidateQueries(newsQueryKeys.GET_NEWS_ARTICLE(customerId, id))
  },
  all(customerId?: EntityId) {
    invalidateNews.getFeed(customerId)
    invalidateNews.getCustomerNews(customerId)
    invalidateNews.getAdminNews()
  },
  getAdminNewsArticle(id: EntityId) {
    queryClient.invalidateQueries(newsQueryKeys.GET_ADMIN_NEWS_ARTICLE(id))
  },
}

// Hooks - User feed
export const useInfiniteNewsFeed = (
  customerId: EntityId,
  filter?: INewsArticleFilter,
  pageSize: number = apiConsts.PageSize,
) => {
  return useInfiniteScroll<INewsArticleListItem>({
    queryUrl: newsEndpoints.getFeed(customerId),
    queryParams: { ...filter },
    pageSize,
    generateQueryKey: (page, ps) => newsQueryKeys.GET_NEWS_FEED(customerId, { ...filter, page, pageSize: ps }),
  })
}

// Hooks - Customer admin
export const useGetCustomerNews = (
  customerId: EntityId,
  filter?: INewsArticleFilter,
  page: number = apiConsts.Page,
  pageSize: number = apiConsts.PageSize,
) =>
  useGet<IPaginatedResult<INewsArticleListItem>>(
    newsEndpoints.getCustomerNews(customerId),
    { ...filter, page, pageSize },
    {
      queryKey: newsQueryKeys.GET_CUSTOMER_NEWS(customerId, { ...filter, page, pageSize }),
    },
  )

export const useGetNewsArticle = (customerId: EntityId, id: EntityId, options?: { enabled?: boolean }) =>
  useGet<INewsArticle>(newsEndpoints.getCustomerNewsArticle(customerId, id), undefined, {
    queryKey: newsQueryKeys.GET_NEWS_ARTICLE(customerId, id),
    enabled: options?.enabled ?? true,
  })

export const useCreateCustomerNews = (customerId: EntityId) =>
  usePost<INewsArticle, ICreateNewsArticle>(newsEndpoints.createCustomerNews(customerId))

export const useUpdateCustomerNews = (customerId: EntityId, id: EntityId) =>
  usePut<INewsArticle, IUpdateNewsArticle>(newsEndpoints.updateCustomerNews(customerId, id))

export const useDeleteCustomerNews = (customerId: EntityId) =>
  useDelete<INewsArticle>(newsEndpoints.deleteCustomerNews(customerId))

export const useSetNewsArticleStatus = (customerId: EntityId) =>
  useMutation(({ id, isActive }: { id: EntityId; isActive: boolean }) =>
    requestProvider.patch(newsEndpoints.setNewsArticleStatus(customerId, id), { isActive }),
  )

export const useSetNewsArticlePinned = (customerId: EntityId) =>
  useMutation(({ id, isPinned }: { id: EntityId; isPinned: boolean }) =>
    requestProvider.patch(newsEndpoints.setPinNewsArticle(customerId, id), { isPinned }),
  )

// Hooks - System admin
export const useGetAdminNews = (
  filter?: INewsArticleFilter,
  page: number = apiConsts.Page,
  pageSize: number = apiConsts.PageSize,
) =>
  useGet<IPaginatedResult<INewsArticleListItem>>(
    newsEndpoints.getAdminNews(),
    { ...filter, page, pageSize },
    {
      queryKey: newsQueryKeys.GET_ADMIN_NEWS({ ...filter, page, pageSize }),
    },
  )

export const useGetAdminNewsArticle = (id: EntityId, options?: { enabled?: boolean }) =>
  useGet<INewsArticle>(newsEndpoints.getAdminNewsArticle(id), undefined, {
    enabled: options?.enabled ?? true,
    queryKey: newsQueryKeys.GET_ADMIN_NEWS_ARTICLE(id),
  })

export const useCreateAdminNews = () => usePost<INewsArticle, ICreateNewsArticle>(newsEndpoints.createAdminNews())

export const useUpdateAdminNews = (id: EntityId) =>
  usePut<INewsArticle, IUpdateNewsArticle>(newsEndpoints.updateAdminNews(id))

export const useDeleteAdminNews = () => useDelete<INewsArticle>(newsEndpoints.deleteAdminNews())

export const useSetAdminNewsArticleStatus = () =>
  useMutation(({ id, isActive }: { id: EntityId; isActive: boolean }) =>
    requestProvider.patch(newsEndpoints.setAdminNewsArticleStatus(id), { isActive }),
  )

export const useSetAdminNewsArticlePinned = () =>
  useMutation(({ id, isPinned }: { id: EntityId; isPinned: boolean }) =>
    requestProvider.patch(newsEndpoints.setPinAdminNewsArticle(id), { isPinned }),
  )
